import { browserFetch } from '../../services/browserSession';
// src/components/reservations/BusinessReservations.js

import React, { useState, useEffect, useCallback } from 'react';
// import { } from 'framer-';
import {
    FaUser,
      FaClock,
  FaFilter,
  FaInfoCircle,
  FaExclamationTriangle,
      FaTag,
  } from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../auth/AuthContext';
import './BusinessReservations.scss';

const BusinessReservations = () => {
  const { currentUser } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    offer_id: ''
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [offers, setOffers] = useState([]);

  // Cargar las ofertas del negocio para el filtro
  useEffect(() => {
    const fetchOffers = async () => {
      try {

        const response = await browserFetch('/api/offers/collab/');

        if (!response.ok) {
          throw new Error('Error al cargar las ofertas');
        }

        const data = await response.json();
        setOffers(data);
      } catch (err) {
        console.error('Error fetching offers:', err);
      }
    };

    if (currentUser?.user_type === 'business') {
      fetchOffers();
    }
  }, [currentUser]);

  const fetchReservations = useCallback(async (newPage = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Verificar que el usuario sea un negocio
      if (currentUser?.user_type !== 'business') {
        throw new Error('Solo los negocios pueden ver las reservas de sus ofertas');
      }

      // Construir parámetros de consulta
      const queryParams = new URLSearchParams();
      queryParams.append('page', newPage);

      if (filters.status) queryParams.append('status', filters.status);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.offer_id) queryParams.append('offer_id', filters.offer_id);

      const response = await browserFetch(`/api/reservations/?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error('Error al cargar las reservaciones');
      }

      const data = await response.json();

      // Verificar si la respuesta tiene la estructura esperada
      const results = Array.isArray(data) ? data : (data.results || []);

      if (newPage === 1) {
        setReservations(results);
      } else {
        setReservations(prev => [...prev, ...results]);
      }

      // Verificar si hay más páginas
      setHasMore(data.next !== null && data.next !== undefined);
      setPage(newPage);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Error al cargar las reservaciones: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, filters]);

  useEffect(() => {
    fetchReservations(1);
  }, [fetchReservations]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      offer_id: ''
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-badge pending';
      case 'confirmed': return 'status-badge confirmed';
      case 'cancelled': return 'status-badge cancelled';
      default: return 'status-badge';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      default: return status || 'Desconocido';
    }
  };

  // Verificar si el usuario es un negocio
  if (currentUser?.user_type !== 'business') {
    return (
      <div className="business-reservations-container">
        <div className="error-state">
          <FaExclamationTriangle />
          <h3>Acceso denegado</h3>
          <p>Solo los negocios pueden ver las reservas de sus ofertas</p>
        </div>
      </div>
    );
  }

  if (loading && page === 1) {
    return (
      <div className="business-reservations-container">
        <div className="loading-state">
          <div className="spinner" />
          <p>Cargando reservaciones...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-reservations-container">
        <div className="error-state">
          <FaInfoCircle />
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => fetchReservations(1)}>Intentar de nuevo</button>
        </div>
      </div>
    );
  }

  return (
    <div className="business-reservations-container">
      <div className="reservations-header">
        <h1>Reservas de Mis Ofertas</h1>
        <button
          className="filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel show">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Oferta</label>
              <select
                name="offer_id"
                value={filters.offer_id}
                onChange={handleFilterChange}
              >
                <option value="">Todas las ofertas</option>
                {offers.map(offer => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title || offer.type_display || 'Oferta sin título'}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Estado</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="confirmed">Confirmada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Fecha inicio</label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>Fecha fin</label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="filters-actions">
            <button onClick={resetFilters}>Limpiar filtros</button>
          </div>
        </div>
      )}

      {reservations.length === 0 ? (
        <div className="empty-state">
          <FaInfoCircle />
          <h3>No hay reservaciones</h3>
          <p>Aún no hay reservaciones para tus ofertas</p>
        </div>
      ) : (
        <>
          <div className="reservations-grid">
            {reservations.map(reservation => (
              <div key={reservation.id} className="reservation-card">
                <div className="card-header">
                  <h3>{reservation.offer_details?.title || 'Oferta sin título'}</h3>
                  <span className={getStatusBadgeClass(reservation.status)}>
                    {getStatusLabel(reservation.status)}
                  </span>
                </div>

                <div className="card-body">
                  <div className="reservation-details">
                    <div className="detail-item">
                      <FaUser />
                      <div>
                        <strong>Creador</strong>
                        <p>{reservation.creator_details?.full_name || reservation.creator_details?.username || 'No especificado'}</p>
                        {reservation.creator_details?.email && (
                          <small>{reservation.creator_details.email}</small>
                        )}
                      </div>
                    </div>

                    <div className="detail-item">
                      <FaClock />
                      <div>
                        <strong>Fecha y hora</strong>
                        <p>
                          {formatDateTime(reservation.start_datetime)} - {formatDateTime(reservation.end_datetime)}
                        </p>
                      </div>
                    </div>

                    <div className="detail-item">
                      <FaTag />
                      <div>
                        <strong>Tipo de oferta</strong>
                        <p>{reservation.offer_details?.type_display || 'No especificado'}</p>
                      </div>
                    </div>

                    {reservation.notes && (
                      <div className="detail-item notes">
                        <FaInfoCircle />
                        <div>
                          <strong>Notas adicionales</strong>
                          <p>{reservation.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Las reservas se crean automáticamente confirmadas.
                      Solo los creadores pueden cancelar sus propias reservas. */}
                </div>
              </div>
            ))}
          </div>

          {loading && (
            <div className="loading-more">
              <div className="spinner" />
              <p>Cargando más reservaciones...</p>
            </div>
          )}

          {hasMore && !loading && (
            <div className="load-more">
              <button onClick={() => fetchReservations(page + 1)}>
                Cargar más reservaciones
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BusinessReservations;
